import type { OrganizationPublic } from "@/client"
import { OrganizationsService } from "@/client"
import { getCurrentOrgId } from "@/utils"
import { useQuery, useQueryClient } from "@tanstack/react-query"

export const DEFAULT_STUDENT_LABELS = {
  dashboardLabel: "Dashboard",
  quizzesLabel: "Take Quiz",
  practiceQuizzesLabel: "Practice Quizzes",
  attemptsLabel: "My Attempts",
  feedbackLabel: "My Feedback",
  flashcardsLabel: "Flashcards",
  settingsLabel: "User Settings",
} as const

type StudentLabels = {
  dashboardLabel: string
  quizzesLabel: string
  practiceQuizzesLabel: string
  attemptsLabel: string
  feedbackLabel: string
  flashcardsLabel: string
  settingsLabel: string
}

export function getStudentLabels(settings: unknown): StudentLabels {
  if (!settings || typeof settings !== "object") return DEFAULT_STUDENT_LABELS
  const labels = (settings as { student_labels?: unknown }).student_labels
  if (!labels || typeof labels !== "object") return DEFAULT_STUDENT_LABELS
  const configured = labels as Record<string, unknown>
  return {
    dashboardLabel: getLabel(
      configured.dashboard_label,
      DEFAULT_STUDENT_LABELS.dashboardLabel,
    ),
    quizzesLabel: getLabel(
      configured.quizzes_label,
      DEFAULT_STUDENT_LABELS.quizzesLabel,
    ),
    practiceQuizzesLabel: getLabel(
      configured.practice_quizzes_label,
      DEFAULT_STUDENT_LABELS.practiceQuizzesLabel,
    ),
    attemptsLabel: getLabel(
      configured.attempts_label,
      DEFAULT_STUDENT_LABELS.attemptsLabel,
    ),
    feedbackLabel: getLabel(
      configured.feedback_label,
      DEFAULT_STUDENT_LABELS.feedbackLabel,
    ),
    flashcardsLabel: getLabel(
      configured.flashcards_label,
      DEFAULT_STUDENT_LABELS.flashcardsLabel,
    ),
    settingsLabel: getLabel(
      configured.settings_label,
      DEFAULT_STUDENT_LABELS.settingsLabel,
    ),
  }
}

function getLabel(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback
}

export function useStudentLabels(): StudentLabels {
  const queryClient = useQueryClient()
  const currentOrgId = getCurrentOrgId()
  const cachedOrg = queryClient.getQueryData<OrganizationPublic>([
    "currentOrganization",
    currentOrgId,
  ])
  const { data } = useQuery({
    queryKey: ["currentOrganization", currentOrgId],
    queryFn: () =>
      OrganizationsService.getOrganization({ orgId: currentOrgId as string }),
    enabled: Boolean(currentOrgId) && !cachedOrg,
    staleTime: 5 * 60 * 1000,
  })
  return getStudentLabels((data ?? cachedOrg)?.settings)
}
